import * as React from 'react';
import * as _ from 'lodash';
import {
  API,
  getUuidV4,
  Logging,
  TemplateCollection,
} from '@vcd-ecm-ui/Common';
import {
  Action,
  ActionDivider,
  ActionMenu,
  GenericGrid,
  mergeDefaultAndCustomColumns,
} from '@vcd-ecm-ui/GenericGrid';
import { VtxColumnProps } from '@vertexinc/vtx-ui-react-component-library';
import TemplatesProps from './TemplatesProps.interface';
import Template from '../Template/Template';
import GridRowData, { GridDataNativeType } from './GridRowData';
import getDefaultColumns from './defaultColumns';
import '../../common-correspondence.scss';
import '../templates.scss';
import useConfiguration from '../../Configuration/useConfiguration';

const Templates = ({
  gatewayEndPoint,
}: TemplatesProps): JSX.Element => {
  const [configuration, configurationNotification] = useConfiguration(gatewayEndPoint, null);

  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [editTemplate, setEditTemplate] = React.useState(false);
  const [displayTemplate, setDisplayTemplate] = React.useState(false);

  const onPreview = (actionKey: string, row: GridRowData): void => {
    Logging.Info('Template Preview:', row.rowData.templateUuid);
    setSelectedTemplate(row.rowData);
    setEditTemplate(false);
    setDisplayTemplate(true);
  };

  const onEdit = (actionKey: string, row: GridRowData): void => {
    Logging.Info('Template Edit:', row.rowData.templateUuid);
    setSelectedTemplate(row.rowData);
    setEditTemplate(true);
    setDisplayTemplate(true);
  };

  const onActivate = (actionKey: string, row: GridRowData): void => {
    Logging.Info('Template Activate:', row.rowData.templateUuid);
    setSelectedTemplate(row.rowData);
  };

  const onDeactivate = (actionKey: string, row: GridRowData): void => {
    Logging.Info('Template Deactivate:', row.rowData.templateUuid);
    setSelectedTemplate(row.rowData);
  };

  const onDelete = (actionKey: string, row: GridRowData): void => {
    Logging.Info('Template Delete:', row.rowData.templateUuid);
    setSelectedTemplate(row.rowData);
  };

  const onPreviewTemplateClose = (): void => {
    setDisplayTemplate(false);
    setSelectedTemplate(null);
  };

  // Get the Action Menu Actions for the Row
  const getActionMenuActions = (row: GridRowData): (Action | ActionDivider)[] => {
    const actions: (Action | ActionDivider)[] = [
      {
        key: 'preview',
        name: 'Preview',
        onClick: onPreview,
        row,
      },
      /*
      new ActionDivider(),
      {
        key: 'edit',
        name: 'Edit',
        onClick: onEdit,
        row,
        disabled: true,
      },
      new ActionDivider(),
      {
        key: 'deactivate',
        name: 'Deactivate',
        onClick: onDeactivate,
        row,
        disabled: true,
      },
      */
    ];

    return actions;
  };

  // Get the Columns for the Grid
  const getColumns = (): VtxColumnProps<GridRowData>[] => {
    // Get the Default Columns
    const defaultColumns: VtxColumnProps<GridRowData>[] = getDefaultColumns();

    // Column Columns with Dynamic Renderer or Filters
    const customColumns = [
      {
        dataIndex: 'action',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => <ActionMenu actions={getActionMenuActions(row)} />,
      },
    ];

    // Merge the Custom Columns into the Default Columns
    return mergeDefaultAndCustomColumns(defaultColumns, customColumns);
  };

  // Parse each row from the Native Data Type to the Actual Data Type
  const rowParser = (row: GridDataNativeType): GridRowData => GridRowData.fromData(row);

  // The API Call used to retrieve data for the Grid
  const apiGetRowsClientModel = (): Promise<TemplateCollection> => {
    const api = new API(gatewayEndPoint);

    return api.correspondence.getTemplatesV41();
  };

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Templates: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setSelectedTemplate(null);
      setEditTemplate(false);
      setDisplayTemplate(false);
    };
  }, []);

  return (
    <>
      <div className="correspondenceContainer">
        <div className="correspondenceHeader">
          Templates
        </div>
        <div className="correspondenceContent">
          <GenericGrid<GridRowData>
            id="templatesGrid"
            dataTypeDescription="Templates"
            getDefaultColumns={getColumns}
            rowParser={rowParser}
            apiGetRowsClientModel={apiGetRowsClientModel}
            model="client"
            verticalHeightOffset={385}
          />
        </div>
        {
          (displayTemplate && selectedTemplate)
          && (
            <Template
              gatewayEndPoint={gatewayEndPoint}
              template={selectedTemplate}
              configuration={configuration}
              isOpen
              onClose={onPreviewTemplateClose}
              canEdit={editTemplate}
            />
          )
        }
      </div>
    </>
  );
};

export default Templates;
